async function request(url, opts = {}) {
  const res = await fetch(url, {
    headers: { ...(opts.rawBody ? {} : { 'Content-Type': 'application/json' }), ...opts.headers },
    method: opts.method || 'GET',
    body: opts.rawBody ? opts.body : (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    const e = new Error(err.error || `HTTP ${res.status}`);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

export const api = {
  me: () => request('/api/me'),
  login: (username, password) => request('/api/login', { method: 'POST', body: { username, password } }),
  register: (username, password) => request('/api/register', { method: 'POST', body: { username, password } }),
  changePassword: (username, currentPassword, newPassword) => request('/api/change-password', { method: 'POST', body: { username, currentPassword, newPassword } }),
  logout: () => request('/api/logout', { method: 'POST' }),

  agents: () => request('/api/agents'),
  models: () => request('/api/models'),

  rooms: () => request('/api/rooms'),
  room: (id) => request(`/api/rooms/${id}`),
  createRoom: (data) => request('/api/rooms', { method: 'POST', body: data }),
  deleteRoom: (id) => request(`/api/rooms/${id}`, { method: 'DELETE' }),
  addRoomMember: (roomId, participant) => request(`/api/rooms/${roomId}/members`, { method: 'POST', body: { participant } }),
  removeRoomMember: (roomId, participant) => request(`/api/rooms/${roomId}/members/${encodeURIComponent(participant)}`, { method: 'DELETE' }),
  roomMessages: (roomId, opts = {}) => {
    const params = new URLSearchParams(Object.fromEntries(Object.entries(opts).filter(([, v]) => v != null)));
    return request(`/api/rooms/${roomId}/messages?${params}`);
  },
  roomMemory: (roomId) => request(`/api/rooms/${roomId}/memory`),
  setRoomDefaultContext: (roomId, contextId) => request(`/api/rooms/${roomId}/default-context`, { method: 'PUT', body: { contextId } }),

  roomTasks: (roomId) => request(`/api/rooms/${roomId}/tasks`),
  roomActiveRuns: (roomId) => request(`/api/rooms/${roomId}/active-runs`),
  task: (id) => request(`/api/tasks/${id}`),
  cancelTask: (id) => request(`/api/tasks/${id}/cancel`, { method: 'POST' }),
  retryTask: (id) => request(`/api/tasks/${id}/retry`, { method: 'POST' }),
  cancelRun: (id) => request(`/api/runs/${id}/cancel`, { method: 'POST' }),

  roomApprovals: (roomId) => request(`/api/rooms/${roomId}/approvals`),
  decideApproval: (id, decision, scope) => request(`/api/approvals/${id}/decide`, { method: 'POST', body: { decision, scope } }),

  humanRequests: () => request('/api/human-requests'),
  answerHumanRequest: (id, answer) => request(`/api/human-requests/${id}/answer`, { method: 'POST', body: { answer } }),

  contexts: () => request('/api/contexts'),

  upload: (roomId, file) => request(`/api/rooms/${roomId}/uploads`, {
    method: 'POST', rawBody: true, body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Filename': encodeURIComponent(file.name || 'file') },
  }),
  roomArtifacts: (roomId) => request(`/api/rooms/${roomId}/artifacts`),
  artifactUrl: (id) => `/api/artifacts/${id}`,

  // admin
  adminUsers: () => request('/api/admin/users'),
  adminActivate: (userId) => request(`/api/admin/activate/${userId}`, { method: 'POST' }),
  adminAgents: () => request('/api/admin/agents'),
  adminCreateAgent: (data) => request('/api/admin/agents', { method: 'POST', body: data }),
  adminUpdateAgent: (id, data) => request(`/api/admin/agents/${id}`, { method: 'PUT', body: data }),
  adminDisableAgent: (id) => request(`/api/admin/agents/${id}/disable`, { method: 'POST' }),
  adminEnableAgent: (id) => request(`/api/admin/agents/${id}/enable`, { method: 'POST' }),
  adminRuns: () => request('/api/admin/runs'),
  adminUsage: () => request('/api/admin/usage'),
  adminRunLog: (id) => request(`/api/admin/runs/${id}/log`),
  adminRules: () => request('/api/admin/permission-rules'),
  adminRevokeRule: (id) => request(`/api/admin/permission-rules/${id}/revoke`, { method: 'POST' }),
  adminCreateContext: (data) => request('/api/admin/contexts', { method: 'POST', body: data }),
  adminAudit: () => request('/api/admin/audit'),
};
